import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import TrustBlock from "../components/TrustBlock";
import LeadSignup from "../components/LeadSignup";
import PromoVideo from "../components/PromoVideo";
import SafeImage from "../components/SafeImage";

const DEFAULT_BANNER = "/hero-banner.jpg";

const resolveLink = (b) => {
    if (!b) return "/produtos";
    if (b.link_type === "product" && b.link_value) return `/produtos/${b.link_value}`;
    if (b.link_type === "category" && b.link_value) return `/produtos?categoria=${b.link_value}`;
    return b.link_value || "/produtos";
};

const SectionHeader = ({ overline, title, action, testid }) => (
    <div className="flex items-end justify-between gap-4 mb-8" data-testid={`section-header-${testid}`}>
        <div>
            {overline && <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-2">{overline}</div>}
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl lg:text-4xl text-brand-ink tracking-tight">{title}</h2>
        </div>
        {action && (
            <Link to={action.to} data-testid={`section-link-${testid}`} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-brand-ink hover:text-brand-gold transition-colors whitespace-nowrap">
                {action.label} <ArrowRight className="w-4 h-4" />
            </Link>
        )}
    </div>
);

const Hero = ({ banner, settings }) => {
    const image = banner?.image || DEFAULT_BANNER;
    const title = banner?.title || settings?.hero_title || "Vitrine Deus Proverá";
    const subtitle = banner?.subtitle || settings?.hero_subtitle || "Estilo, qualidade e propósito";
    const btnText = banner?.button_text || "Ver produtos";
    const link = resolveLink(banner);
    const isInternal = link.startsWith("/");

    return (
        <section data-testid="hero-section" className="relative bg-brand-ink overflow-hidden">
            <div className="w-full bg-brand-ink flex items-center justify-center">
                <img src={image} alt={title} loading="eager" fetchPriority="high" decoding="async" className="block w-full h-auto max-h-[92vh] object-contain mx-auto" data-testid="hero-banner-image" />
            </div>
            <div className="bg-brand-ink border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-center sm:text-left">
                        <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-1.5">Coleção oficial</div>
                        <p className="text-white/85 text-sm sm:text-base font-light max-w-md">{subtitle}. Compra segura nas plataformas oficiais.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        {isInternal ? (
                            <Link to={link} data-testid="hero-cta-products" className="inline-flex items-center justify-center gap-2 bg-brand-gold text-white hover:bg-brand-goldHover px-7 py-3.5 font-heading font-semibold rounded-sm">{btnText} <ArrowRight className="w-4 h-4" /></Link>
                        ) : (
                            <a href={link} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-products" className="inline-flex items-center justify-center gap-2 bg-brand-gold text-white hover:bg-brand-goldHover px-7 py-3.5 font-heading font-semibold rounded-sm">{btnText} <ArrowRight className="w-4 h-4" /></a>
                        )}
                        <Link to="/categorias" data-testid="hero-cta-categories" className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white hover:text-brand-ink px-7 py-3.5 font-heading font-semibold rounded-sm">Explorar categorias</Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

const CategoriesSection = ({ categories }) => (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHeader overline="Navegue por" title="Categorias" action={{ to: "/categorias", label: "Ver todas" }} testid="categories" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="categories-grid">
            {categories.map((c) => (
                <Link key={c.id} to={`/produtos?categoria=${c.slug}`} data-testid={`category-card-${c.slug}`} className="group relative aspect-square overflow-hidden bg-brand-ink rounded-sm">
                    <SafeImage
                        src={c.image}
                        alt={c.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-contain p-3 sm:p-5 opacity-95 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        fallback={<div className="absolute inset-0 flex items-center justify-center text-brand-gold/40 text-4xl font-heading uppercase tracking-widest">{c.name?.[0] || "V"}</div>}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent pointer-events-none" />
                    <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                        <h3 className="font-heading text-white font-semibold text-base sm:text-lg leading-tight">{c.name}</h3>
                        <div className="mt-1 inline-flex items-center gap-1 text-xs text-brand-gold font-semibold">Explorar <ChevronRight className="w-3 h-3" /></div>
                    </div>
                </Link>
            ))}
        </div>
    </section>
);

const NovidadesSection = ({ items }) => (
    <section className="bg-white border-y border-brand-border py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader overline="Recém-chegados" title="Novidades" action={{ to: "/produtos?filter=novidades", label: "Ver todas" }} testid="novidades" />
        </div>
        <div className="md:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pl-4 pr-4">
            {items.map((p) => (
                <div key={p.id} className="snap-start shrink-0 w-[78%]"><ProductCard product={p} /></div>
            ))}
        </div>
        <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-6">
                {items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
        </div>
    </section>
);

const BestsellersSection = ({ items }) => (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHeader overline="Favoritos do público" title="Mais vendidos" action={{ to: "/produtos?filter=mais-vendidos", label: "Ver todos" }} testid="bestsellers" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
    </section>
);

const FeaturedSection = ({ items }) => (
    <section data-testid="featured-editorial" className="bg-brand-ink text-white py-16 md:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Produtos em destaque</div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">Um propósito em cada peça.</h2>
                <p className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed max-w-md">Selecionamos cada item da nossa vitrine pensando em estilo, qualidade e na mensagem que ele carrega.</p>
                <Link to="/produtos" data-testid="featured-cta" className="mt-8 inline-flex items-center gap-2 bg-brand-gold text-white hover:bg-brand-goldHover px-7 py-4 font-heading font-semibold rounded-sm">
                    Ver todos os produtos <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {items.slice(0, 4).map((p, i) => (
                    <Link key={p.id} to={`/produtos/${p.slug}`} className={`group relative overflow-hidden bg-brand-dark rounded-sm ${i === 0 ? "row-span-2 aspect-[3/5]" : "aspect-square"}`}>
                        <SafeImage
                            src={p.images?.[0]}
                            alt={p.name}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            fallback={<div className="absolute inset-0 flex items-center justify-center bg-brand-bg text-brand-gold/40 text-3xl font-heading">{p.name?.[0]?.toUpperCase() || "V"}</div>}
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-bold">{p.category_slug}</div>
                            <div className="font-heading font-semibold text-sm sm:text-base text-white mt-1 line-clamp-1">{p.name}</div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    </section>
);

const Home = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [banner, setBanner] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/products"),
            api.get("/categories"),
            api.get("/banners?type=primary"),
            api.get("/settings"),
        ])
            .then(([p, c, b, s]) => {
                setProducts(p.data);
                setCategories(c.data);
                setBanner(b.data?.[0] || null);
                setSettings(s.data);
            })
            .finally(() => setLoading(false));
        // Track page view
        api.post("/track", { type: "page_view", page: "home" }).catch(() => {});
    }, []);

    if (loading) return <div className="text-center py-20 text-brand-inkSoft" data-testid="home-loading">Carregando…</div>;

    const featured = products.filter((p) => p.is_featured).slice(0, 4);
    const bestsellers = products.filter((p) => p.is_bestseller).slice(0, 4);
    const novidades = products.filter((p) => p.is_new).slice(0, 6);

    const vis = settings?.section_visibility || { categories: true, novidades: true, bestsellers: true, featured: true, leads: true, trust: true, promo_video: true };
    const order = settings?.section_order || ["promo_video", "categories", "novidades", "bestsellers", "featured"];

    const sectionMap = {
        promo_video: vis.promo_video && settings?.promo_video_enabled ? <PromoVideo key="pv" settings={settings} /> : null,
        categories: vis.categories && categories.length > 0 ? <CategoriesSection key="cats" categories={categories} /> : null,
        novidades: vis.novidades && novidades.length > 0 ? <NovidadesSection key="nov" items={novidades} /> : null,
        bestsellers: vis.bestsellers && bestsellers.length > 0 ? <BestsellersSection key="best" items={bestsellers} /> : null,
        featured: vis.featured && featured.length > 0 ? <FeaturedSection key="feat" items={featured} /> : null,
    };

    return (
        <div data-testid="home-page">
            <Hero banner={banner} settings={settings} />
            {vis.trust !== false && <TrustBlock />}
            {order.map((k) => sectionMap[k]).filter(Boolean)}
            {vis.leads !== false && <LeadSignup />}
        </div>
    );
};

export default Home;
